import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import {
  getPlanInfo,
  getMySubscription,
  createSubscription,
  verifySubscription,
  cancelSubscription,
  loadRazorpay,
  paypalGetPlanId,
  paypalRecordSubscription,
} from '../../actions/subscriptionAction';
import { billingRegion } from '../../utils/region';
import './Subscribe.css';

const PAYPAL_CLIENT_ID = 'AcG8nflgS3n2HfX8n6-SDhZlJQS4RkWXILKaygXfrpk4mGiW2XxqU1tMk7ams_kaTUlbirFHouGRyuLY';

const FEATURES = [
  'Dmyor Assist: AI chat that plans, breaks down, and drafts',
  'Focus Mode: timed deep-work sessions on any todo',
  'Unlimited todos and habits across every vision',
  'AI-generated documents in the Library',
  'Execution summaries of where your vision stands',
];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

export default function Subscribe() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((s) => s.user || {});

  const [interval, setInterval] = useState('monthly');
  const [region, setRegion] = useState(null); // { symbol, method, monthly, yearly }
  const [sub, setSub] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [manageable, setManageable] = useState(false);
  const [freeLimit, setFreeLimit] = useState(0);
  const [freeRemaining, setFreeRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [p, m] = await Promise.all([
        dispatch(getPlanInfo()),
        dispatch(getMySubscription()),
      ]);
      setRegion(p.regions?.[billingRegion()] || p.regions?.INTL || null);
      setSub(m.subscription);
      setIsAdmin(!!m.isAdmin);
      setSubscribed(!!m.subscribed);
      setManageable(!!m.manageable);
      setFreeLimit(m.freeLimit || 0);
      setFreeRemaining(typeof m.freeRemaining === 'number' ? m.freeRemaining : 0);
    } catch (_) {}
    setLoading(false);
  }, [dispatch]);

  useEffect(() => { refresh(); }, [refresh]);

  const subscribe = async () => {
    if (busy) return;
    if (!isAuthenticated) { navigate('/login?next=/dashboard/upgrade'); return; }
    setBusy(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error('Could not load payment. Check your connection.'); setBusy(false); return; }
      const d = await dispatch(createSubscription(interval));
      if (d.reused) { await refresh(); toast.success('You already have an active plan.'); setBusy(false); return; }

      let completed = false;

      const rzp = new window.Razorpay({
        key: d.key,
        subscription_id: d.subscriptionId,
        name: 'dmyor',
        description: 'dmyor Pro subscription',
        prefill: { name: user?.name || '', email: user?.email || '' },
        theme: { color: '#f17501' },
        handler: async (response) => {
          completed = true;
          try {
            await dispatch(verifySubscription({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_signature: response.razorpay_signature,
            }));
            toast.success('You are on Pro. AI features are unlocked.');
            await refresh();
          } catch (_) {
            toast.error('Payment captured but verification is pending. It will update shortly.');
            await refresh();
          }
        },
        modal: {
          ondismiss: async () => {
            if (!completed) {
              try { await dispatch(cancelSubscription()); } catch (_) {}
              await refresh();
            }
            setBusy(false);
          },
        },
      });
      rzp.open();
      setBusy(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not start subscription.');
      setBusy(false);
    }
  };

  const doCancel = async () => {
    if (cancelling) return;
    setCancelling(true);
    try {
      await dispatch(cancelSubscription());
      toast.success('Your subscription has been cancelled.');
      setShowCancelModal(false);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not cancel. Try again.');
    }
    setCancelling(false);
  };

  // Create a recurring PayPal subscription using the server-configured plan id.
  const handlePaypalCreateSub = async (data, actions) => {
    try {
      const { planId } = await dispatch(paypalGetPlanId(interval));
      if (!planId) throw new Error('No plan id');
      return actions.subscription.create({ plan_id: planId });
    } catch (err) {
      console.error('PayPal createSubscription failed:', err?.response?.status, err?.response?.data || err.message);
      toast.error('Could not start PayPal subscription.');
      throw err;
    }
  };

  const handlePaypalApprove = async (data) => {
    try {
      await dispatch(paypalRecordSubscription(data.subscriptionID, interval));
      toast.success('You are on Pro. AI features are unlocked.');
      await refresh();
    } catch (_) {
      toast.error('Subscription approved but recording failed. It will sync shortly.');
      await refresh();
    }
  };

  const statusLine = () => {
    if (!sub) return null;
    if (sub.cancelAtPeriodEnd) return `Access ends on ${fmtDate(sub.currentPeriodEnd)}.`;
    if (sub.currentPeriodEnd) return `Renews on ${fmtDate(sub.currentPeriodEnd)}.`;
    if (sub.status === 'active' || sub.status === 'authenticated') return 'Active. Unlimited AI requests.';
    return null;
  };

  const cur = region?.symbol || '$';
  const price = region ? (interval === 'yearly' ? region.yearly : region.monthly) : null;
  const payMethod = region?.method || 'paypal';   // India → razorpay, else → paypal

  return (
    <div className="sub-page">
      <Helmet><title>dmyor Pro</title></Helmet>
      <div className="sub-wrap">

        {loading ? (
          <div className="sub-loading">Loading…</div>
        ) : isAdmin && !manageable ? (
          <>
            <div className="sub-label">Admin</div>
            <h1 className="sub-title">Full access</h1>
            <p className="sub-sub">You have access to every AI feature as an admin. No subscription needed.</p>
            <ul className="sub-feature-list">
              {FEATURES.map(f => <li key={f}><span className="sub-tick" />{f}</li>)}
            </ul>
          </>
        ) : subscribed ? (
          <>
            <div className="sub-label">dmyor Pro</div>
            <h1 className="sub-title">{sub?.cancelAtPeriodEnd ? 'Ending soon' : "You're on Pro"}</h1>

            <div className="sub-active-card">
              <div className={`sub-badge${sub?.cancelAtPeriodEnd ? ' is-ending' : ''}`}>
                <span className="sub-badge-dot" />
                {sub?.cancelAtPeriodEnd ? 'Cancelled' : 'Active'}
              </div>
              <div className="sub-active-rows">
                {sub?.interval && (
                  <div className="sub-active-row">
                    <span>Plan</span>
                    <span>{sub.interval === 'yearly' ? 'Yearly' : 'Monthly'}</span>
                  </div>
                )}
                {sub?.provider && (
                  <div className="sub-active-row">
                    <span>Billing</span>
                    <span>{sub.provider === 'paypal' ? 'PayPal' : 'Razorpay'}</span>
                  </div>
                )}
                {sub?.currentPeriodEnd && (
                  <div className="sub-active-row">
                    <span>{sub?.cancelAtPeriodEnd ? 'Access ends' : 'Renews'}</span>
                    <span>{fmtDate(sub.currentPeriodEnd)}</span>
                  </div>
                )}
              </div>
            </div>

            <p className="sub-active-heading">Your Pro benefits</p>
            <ul className="sub-feature-list">
              {FEATURES.map(f => <li key={f}><span className="sub-tick" />{f}</li>)}
            </ul>

            {sub?.cancelAtPeriodEnd ? (
              <p className="sub-fine">Your plan is set to cancel. You keep Pro until {fmtDate(sub.currentPeriodEnd)}.</p>
            ) : manageable && !['cancelled', 'completed', 'expired'].includes(sub?.status) && (
              <button className="sub-cancel" onClick={() => setShowCancelModal(true)} disabled={cancelling}>
                Cancel subscription
              </button>
            )}
          </>
        ) : (
          <>
            <div className="sub-label">dmyor Pro</div>
            <h1 className="sub-title">Go unlimited.</h1>
            <p className="sub-sub">
              Start free. Upgrade to Pro to unlock Dmyor Assist, Focus Mode, and unlimited todos and habits across every vision.
            </p>

            {region && (() => {
              const saved = region.yearly && region.monthly
                ? Math.round((1 - (Number(region.yearly) / (Number(region.monthly) * 12))) * 100)
                : 0;
              return (
                <div className="sub-toggle" role="tablist">
                  {['monthly', 'yearly'].map(iv => (
                    <button
                      key={iv}
                      type="button"
                      className={`sub-toggle-opt${interval === iv ? ' is-active' : ''}`}
                      onClick={() => setInterval(iv)}
                    >
                      {iv === 'yearly' ? 'Yearly' : 'Monthly'}
                      {iv === 'yearly' && saved > 0 && <span className="sub-toggle-save">Save {saved}%</span>}
                    </button>
                  ))}
                </div>
              );
            })()}

            {price && (
              <div className="sub-price">
                <span className="sub-price-amt">{cur}{price}</span>
                <span className="sub-price-per">/ {interval === 'yearly' ? 'year' : 'month'}</span>
              </div>
            )}

            <ul className="sub-feature-list">
              {FEATURES.map(f => <li key={f}><span className="sub-tick" />{f}</li>)}
            </ul>

            {payMethod === 'razorpay' ? (
              <>
                <button className="sub-cta" onClick={subscribe} disabled={busy}>
                  {busy ? 'Starting…' : 'Get Pro'}
                </button>
                <p className="sub-fine">Cancel anytime. Billing by Razorpay.</p>
              </>
            ) : (
              <div className="sub-paypal-wrap">
                <PayPalScriptProvider options={{ 'client-id': PAYPAL_CLIENT_ID, vault: true, intent: 'subscription' }}>
                  <PayPalButtons
                    key={interval}
                    style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'subscribe', height: 45 }}
                    createSubscription={handlePaypalCreateSub}
                    onApprove={handlePaypalApprove}
                    onError={(err) => { console.error('PayPal onError:', err); toast.error('PayPal encountered an error.'); }}
                  />
                </PayPalScriptProvider>
                <p className="sub-fine">Recurring subscription. Cancel anytime.</p>
              </div>
            )}
          </>
        )}

      </div>

      {showCancelModal && (
        <div className="sub-modal-overlay" onMouseDown={() => !cancelling && setShowCancelModal(false)}>
          <div className="sub-modal" onMouseDown={e => e.stopPropagation()}>
            <div className="sub-modal-icon"><i className="bx bx-error-circle" /></div>
            <h2 className="sub-modal-title">Cancel dmyor Pro?</h2>
            <p className="sub-modal-text">
              You'll keep full Pro access until{' '}
              <strong>{sub?.currentPeriodEnd ? fmtDate(sub.currentPeriodEnd) : 'the end of your billing period'}</strong>.
              After that you'll drop back to the free tier.
            </p>

            <ul className="sub-modal-losing">
              <li>Unlimited AI requests</li>
              <li>Dmyor Assist chat &amp; breakdowns</li>
              <li>AI documents, habits &amp; summaries</li>
            </ul>

            <div className="sub-modal-actions">
              <button className="sub-modal-keep" onClick={() => setShowCancelModal(false)} disabled={cancelling}>
                Keep Pro
              </button>
              <button className="sub-modal-confirm" onClick={doCancel} disabled={cancelling}>
                {cancelling ? 'Cancelling…' : 'Cancel anyway'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
